import "vue-multiselect/dist/vue-multiselect.min.css";
import { Component, Vue, Watch } from "vue-property-decorator";
import { format, parse, parseISO } from "date-fns";
import Loader from "@/components/Includes/Loader/Loader";
import TableEmpty from "@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty";
import Pagination from "@/components/Includes/Pagination/Pagination";
import { Switch as CSwitch } from "@coreui/vue";
import { namespace, Action } from "vuex-class";
import IPartnerPromoCode from "@/interfaces/IPartnerPromoCode";
import ITableCol from "@/interfaces/ITableCol";
import getTableColumns from "@/libs/getTableColumns";
import ITariff from "@/interfaces/ITariff";
import Multiselect from "vue-multiselect";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import { transformMultiSelect } from "@/libs/transformData/transformMultiSelect";
import urlToId from "@/libs/urlToId";

const parnerPromoCodeModule = namespace("partner/promoCode");

@Component({
  components: {
    CSwitch,
    Loader,
    TableEmpty,
    Pagination,
    Multiselect,
  },
})
export default class PartnerPromoCodes extends Vue {
  @parnerPromoCodeModule.Action("fetchPartnerPromoCodes")
  private readonly fetchPartnerPromoCodesAction!: (params: {
    query: string;
  }) => Promise<{ links: number; data: IPartnerPromoCode[] }>;

  @parnerPromoCodeModule.Action("fetchPartnerPromoCode")
  private readonly fetchPartnerPromoCodeAction!: (params: {
    id: number;
  }) => Promise<IPartnerPromoCode>;

  @parnerPromoCodeModule.Action("updatePartnerPromoCode")
  private readonly updatePartnerPromoCodeAction!: (params: {
    id: number;
    active: boolean;
  }) => Promise<IPartnerPromoCode>;

  @Action("fetchVpnTariffs")
  private readonly fetchVpnTariffsAction!: (params: {
    query: string;
  }) => Promise<{ links: number; data: ITariff[] }>;

  @parnerPromoCodeModule.Action("createPartnerPromoCode")
  private readonly createPartnerPromoCodeAction!: (payload: {
    code?: string;
    startDate?: string;
    endDate?: string;
    maxUsageCount?: number;
    forceCreditCard?: boolean;
    tariff?: string;
    price?: string;
    currency?: string;
    discount?: number;
    active: boolean;
  }) => IPartnerPromoCode;

  private readonly limit = 100;
  private page = 1;
  private isLoading = false;
  private totalItems = 0;
  private partnerPromoCodes: IPartnerPromoCode[] = [];

  private isShowCreateModal = false;
  private editModalId = 0;

  private formData: {
    code?: string;
    startDate: string;
    endDate: string;
    maxUsageCount: number;
    forceCreditCard: boolean;
    tariff: null | IMultiSelectItem;
    price: string;
    currency: string;
    discount: number;
    active: boolean;
  } | null = null;

  private vpnTariffs: IMultiSelectItem[] = [];

  private get isValidForm() {
    if (this.isShowCreateModal) {
      return !!this.formData && !!this.formData.tariff;
    }

    return true;
  }

  private get fields(): ITableCol[] {
    const columns = getTableColumns(this.partnerPromoCodes, false);

    return Array.isArray(columns) ? columns : [];
  }

  private get isShowModal() {
    return this.isShowCreateModal || this.editModalId > 0;
  }

  private set isShowModal(isShow) {
    if (!isShow) {
      this.isShowCreateModal = false;
      this.editModalId = 0;
      this.formData = null;
    }
  }

  private formatDate(date: string) {
    return format(parseISO(date), "dd.MM.yyyy HH:mm:ss");
  }

  private getTariff(url: string) {
    const id = +urlToId(url);
    const tariff = this.vpnTariffs.find((vpnTariff) => vpnTariff.value === id);

    return tariff && `${tariff.label} (ID: ${tariff.value})`;
  }

  private async fetchPartnerPromoCodes() {
    this.isLoading = true;
    try {
      const { data, links } = await this.fetchPartnerPromoCodesAction({
        query: `?pagination=true&page=${this.page}&limit=${this.limit}`,
      });

      this.totalItems = links;
      this.partnerPromoCodes = data;
    } catch {
      this.partnerPromoCodes = [];
      this.totalItems = 0;
      this.page = 1;
    }

    this.isLoading = false;
  }

  private sendFormData() {
    if (this.isShowCreateModal) {
      this.createPartnerPromoCode();
    } else {
      this.updatePartnerPromoCode();
    }
  }

  private async updatePartnerPromoCode() {
    this.isLoading = true;
    if (this.formData && this.editModalId > 0) {
      await this.updatePartnerPromoCodeAction({
        id: this.editModalId,
        active: this.formData.active,
      });
    }

    this.isLoading = false;
    this.isShowModal = false;
    this.fetchPartnerPromoCodes();
  }

  private onRowClicked({ id }: IPartnerPromoCode) {
    this.editModalId = id;
  }

  private async createPartnerPromoCode() {
    if (!this.formData || this.isLoading || !this.isValidForm) {
      return;
    }

    this.isLoading = true;

    const data: {
      discount?: number;
      maxUsageCount?: number;
      tariff?: string;
      endDate?: string;
      startDate?: string;
      code?: string | undefined;
      forceCreditCard: boolean;
      active: boolean;
      price?: string;
      currency?: string;
    } = {
      forceCreditCard: this.formData.forceCreditCard,
      active: this.formData.active,
    };

    if (this.formData.code && this.formData.code.trim().length > 0) {
      data.code = this.formData.code;
    }

    if (this.formData.price && this.formData.price.trim().length > 0) {
      data.price = this.formData.price;
    }

    if (this.formData.currency && this.formData.currency.trim().length > 0) {
      data.currency = this.formData.currency;
    }

    if (+this.formData.discount > -1) {
      data.discount = +this.formData.discount;
    }

    if (+this.formData.maxUsageCount > -1) {
      data.maxUsageCount = +this.formData.maxUsageCount;
    }

    if (this.formData.startDate) {
      data.startDate = format(
        parse(this.formData.startDate, "yyyy-MM-dd", new Date()),
        `yyyy-MM-dd'T'HH:mm:ss`
      );
    }

    if (this.formData.endDate) {
      data.endDate = format(
        parse(this.formData.endDate, "yyyy-MM-dd", new Date()),
        `yyyy-MM-dd'T'HH:mm:ss`
      );
    }

    if (this.formData.tariff) {
      data.tariff = `/api/vpn/tariffs/${this.formData.tariff.value}`;
    }

    try {
      await this.createPartnerPromoCodeAction(data);

      this.fetchPartnerPromoCodes();
      this.isLoading = false;
      this.isShowModal = false;
      this.$notify({
        group: 'notice',
        title: 'Promocode created',
        text: 'Successfully',
        type: 'success'
    });
    } catch (error) {
      console.error(error);
      this.isLoading = false;
    }
  }

  @Watch("isShowModal")
  private async onChangeShowModal() {
    if (this.isShowCreateModal) {
      this.formData = {
        code: "",
        startDate: "",
        endDate: "",
        maxUsageCount: 1,
        forceCreditCard: false,
        tariff: null,
        price: "",
        currency: "USD",
        discount: 0,
        active: true,
      };
    } else if (this.editModalId > 0) {
      this.isLoading = true;
      const {
        code,
        startDate,
        endDate,
        maxUsageCount,
        forceCreditCard,
        tariff,
        price,
        currency,
        discount,
        active,
      } = await this.fetchPartnerPromoCodeAction({
        id: this.editModalId,
      });

      this.formData = {
        code,
        startDate,
        endDate,
        maxUsageCount,
        forceCreditCard,
        tariff:
          this.vpnTariffs.find(
            (vpnTariff) => vpnTariff.value === +urlToId(tariff)
          ) || null,
        price,
        currency,
        discount,
        active,
      };
      this.isLoading = false;
    }
  }

  @Watch("page")
  private onChangePage() {
    this.fetchPartnerPromoCodes();
  }

  private async mounted() {
    this.fetchPartnerPromoCodes();
    const { data } = await this.fetchVpnTariffsAction({
      query: `?pagination=true&page=1&publicKey=SW_PARTNER_&limit=300`,
    });

    this.vpnTariffs = transformMultiSelect(data, "productId", "id");
  }
}
